import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Typography } from '@mui/material';
import {
	deletePgPayabilityRuleRequest,
	getPgPayabilityRulesRequest,
} from 'redux/reducers/pgSettings/reducer';
import {
	selectPgPayabilityRules,
	selectPgPayabilityRulesLoading,
} from 'redux/reducers/pgSettings/selectors';
import { TPgPayabilityRule } from 'services/api/paymentGateway/types';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { EasTable } from 'ui/EasTable';
import { ETableStructureAlign, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import { PgPage } from '../common/PgPage';
import { PgPanel } from '../common/PgPanel';
import { usePgServices } from '../common/hooks';
import { PG_SETTINGS_GROUP } from '../constants';
import { PayabilityRuleDialog } from './PayabilityRuleDialog';
import { TestOnInvoice } from './TestOnInvoice';

const EDIT_ACTION = 'Edit';
const DELETE_ACTION = 'Delete';

type TRuleColumn = TTableStructure<keyof TPgPayabilityRule, TPgPayabilityRule>;

const column = (
	name: string,
	selector: keyof TPgPayabilityRule,
	extra: Partial<TRuleColumn> = {},
): TRuleColumn => ({
	name,
	selector,
	show: true,
	sortable: false,
	filterable: false,
	type: ETableStructureType.STRING,
	align: ETableStructureAlign.LEFT,
	...extra,
});

const channelsOf = (rule: TPgPayabilityRule): string =>
	[
		rule.applyLink && 'link',
		rule.applyDashboard && 'dashboard',
		rule.applyAuto && 'auto',
		rule.applyAdmin && 'admin',
	]
		.filter(Boolean)
		.join(', ') || '—';

/**
 * Settings → Debt transfer rules (decision 12, 03 §4.2): when an invoice may not be paid. Active
 * rules are checked by priority; the first one that blocks wins, its message is shown to the
 * customer. Applies on the payment page, in the dashboard, in the monthly cycle and (optionally)
 * to admin charges.
 */
export const DebtRules: FC = () => {
	const dispatch = useDispatch();
	const rules = useSelector(selectPgPayabilityRules);
	const loading = useSelector(selectPgPayabilityRulesLoading);
	const { services } = usePgServices();

	const [dialogOpen, setDialogOpen] = useState(false);
	const [ruleToEdit, setRuleToEdit] = useState<TPgPayabilityRule | null>(null);
	const [ruleToDelete, setRuleToDelete] = useState<TPgPayabilityRule | null>(null);

	useEffect(() => {
		dispatch(getPgPayabilityRulesRequest());
	}, [dispatch]);

	const tableStructure = useMemo<TRuleColumn[]>(() => {
		const serviceName = (id: number | null) =>
			id === null ? 'All' : (services?.find((s) => s.id === id)?.code ?? `#${id}`);
		return [
			column('Priority', 'priority', { value: (item) => String(item.priority) }),
			column('Name', 'name', { tooltip: (item) => item.message }),
			column('Active', 'isActive', {
				type: ETableStructureType.BOOLEAN,
				align: ETableStructureAlign.CENTER,
			}),
			column('Service', 'serviceId', { value: (item) => serviceName(item.serviceId) }),
			column('Invoice id below', 'invoiceIdBelow', {
				value: (item) => (item.invoiceIdBelow === null ? 'Any' : String(item.invoiceIdBelow)),
			}),
			column('Blocked days', 'blockedFromDay', {
				value: (item) =>
					item.blockedFromDay === null || item.blockedToDay === null
						? '—'
						: `${item.blockedFromDay}–${item.blockedToDay}`,
			}),
			column('Due date in month', 'requireDueDateInCurrentMonth', {
				type: ETableStructureType.BOOLEAN,
				align: ETableStructureAlign.CENTER,
			}),
			column('Channels', 'applyLink', { value: channelsOf }),
			column('Message', 'message', { tooltip: (item) => item.message }),
			{
				name: 'Actions',
				selector: 'actions',
				noName: true,
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.BUTTONS,
				align: ETableStructureAlign.RIGHT,
				buttons: [
					{ name: EDIT_ACTION, icon: EIconButtons.EDIT },
					{ name: DELETE_ACTION, icon: EIconButtons.DELETE },
				],
			},
		];
	}, [services]);

	const handleAddNew = useCallback(() => {
		setRuleToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			const rule = rules?.find((r) => r.id === id) ?? null;
			if (!rule) return;
			if (action === EDIT_ACTION) {
				setRuleToEdit(rule);
				setDialogOpen(true);
			} else if (action === DELETE_ACTION) {
				setRuleToDelete(rule);
			}
		},
		[rules],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setRuleToEdit(null);
	}, []);

	const handleConfirmDelete = () => {
		if (ruleToDelete) dispatch(deletePgPayabilityRuleRequest(ruleToDelete.id));
		setRuleToDelete(null);
	};

	return (
		<PgPage title="Debt Transfer Rules" group={PG_SETTINGS_GROUP}>
			<PgPanel
				title="Rules"
				bodyPadding={0}
				actions={
					<>
						{loading && <CircularProgress size={18} />}
						<Button variant="contained" size="small" onClick={handleAddNew}>
							Add rule
						</Button>
					</>
				}
			>
				<Typography variant="body2" color="text.secondary" sx={{ p: '8px 12px' }}>
					A rule covers invoices of its service (or all services) with an id below the limit, on the
					checked channels. It refuses payment on the blocked days of the month and/or when the due
					date is not in the current month. Active rules are checked by priority; the first refusal
					wins.
				</Typography>
				<EasTable<TPgPayabilityRule>
					tableStructure={tableStructure}
					items={rules ?? []}
					itemsIsLoading={loading && !rules}
					handleAction={handleAction}
					skeletonRows={Array.from(new Array(3))}
				/>
			</PgPanel>
			<TestOnInvoice rules={rules} />
			<PayabilityRuleDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				ruleToEdit={ruleToEdit}
				services={services ?? []}
			/>
			<ConfirmationDialog
				open={!!ruleToDelete}
				setClose={() => setRuleToDelete(null)}
				handleConfirm={handleConfirmDelete}
				title="Delete rule"
				text={`Delete rule <b>#${ruleToDelete?.id ?? ''}</b>? To pause a rule, uncheck “Active” instead.`}
			/>
		</PgPage>
	);
};
